import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, CheckCircle2, Loader2, XCircle, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import apiService from '@/services/apiService';
import { useApp } from '@/context/AppContext';
import { useAnalytics, ANALYTICS_EVENTS } from '@/hooks/useAnalytics';

const SINGLE_STORAGE_KEY = 'pending_document_id';
const BATCH_STORAGE_KEY = 'pending_document_ids';
const VISA_TYPE_KEY = 'batch_visa_type';
const MAX_PAYMENT_RETRIES = 5;
const PAYMENT_RETRY_DELAY = 2000;

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [statusMessage, setStatusMessage] = useState('Verifying payment...');
  const [error, setError] = useState(null);
  const processingRef = useRef(false);
  const { clearQueue } = useApp();
  const { trackEvent } = useAnalytics();
  
  // Get parameters from URL
  const sessionId = searchParams.get('session_id'); // Stripe
  const paypalToken = searchParams.get('token'); // PayPal order ID
  const payerId = searchParams.get('PayerID'); // PayPal payer ID
  const provider = searchParams.get('provider') || localStorage.getItem('payment_provider') || 'stripe';

  useEffect(() => {
    // Prevent double processing
    if (processingRef.current) return;
    processingRef.current = true;
    
    // Track payment completed
    trackEvent(ANALYTICS_EVENTS.PAYMENT_COMPLETED, {
      session_id: sessionId || paypalToken,
      provider: provider
    });

    // Check for batch or single processing
    let pendingIds = null;
    let singlePendingId = null;
    let visaType = null;
    
    try {
      const batchIdsStr = localStorage.getItem(BATCH_STORAGE_KEY);
      if (batchIdsStr) {
        pendingIds = JSON.parse(batchIdsStr);
        visaType = localStorage.getItem(VISA_TYPE_KEY);
      }
      singlePendingId = localStorage.getItem(SINGLE_STORAGE_KEY);
    } catch (e) {
      // localStorage unavailable
    }

    const isBatchMode = pendingIds && pendingIds.length > 0;
    const hasPendingDoc = isBatchMode || singlePendingId;

    // Check for valid payment session
    const hasValidSession = sessionId || (paypalToken && payerId);
    
    if (!hasValidSession) {
      setStatus('error');
      setError('No payment session found. Please try again.');
      return;
    }

    if (!hasPendingDoc) {
      setStatus('error');
      setError('No pending document found. Please upload your document again.');
      return;
    }

    const processDocuments = async () => {
      try {
        // Step 1: Verify/Capture payment
        setStatus('verifying');
        
        if (provider === 'paypal' && paypalToken && payerId) {
          // PayPal: Capture the payment
          setStatusMessage('Capturing PayPal payment...');
          
          try {
            const captureResult = await apiService.capturePayPalOrder(paypalToken, false);
            
            if (!captureResult.completed) {
              throw new Error('PayPal payment capture failed');
            }
            
            setStatusMessage('PayPal payment verified!');
          } catch (captureError) {
            console.error('PayPal capture error:', captureError);
            setStatus('error');
            setError('Failed to capture PayPal payment. Please contact support.');
            return;
          }
        } else if (sessionId) {
          // Stripe: Verify payment with retries
          setStatusMessage('Verifying Stripe payment...');
          
          let paymentVerified = false;
          for (let retry = 0; retry < MAX_PAYMENT_RETRIES; retry++) {
            try {
              const paymentStatus = await apiService.getPaymentStatus(sessionId);
              if (paymentStatus.is_paid || paymentStatus.payment_status === 'paid') {
                paymentVerified = true;
                break;
              }
            } catch (e) {
              console.log(`Payment verification attempt ${retry + 1} failed`);
            }
            
            if (retry < MAX_PAYMENT_RETRIES - 1) {
              await new Promise(resolve => setTimeout(resolve, PAYMENT_RETRY_DELAY));
            }
          }

          if (!paymentVerified) {
            // Try proceeding anyway - Stripe webhooks may have confirmed
            console.log('Payment verification retries exhausted, proceeding with processing');
          }
        }

        // Step 2: Process documents
        setStatus('processing');
        
        if (isBatchMode) {
          // Batch processing
          setStatusMessage(`Analyzing ${pendingIds.length} document(s)...`);
          
          const result = await apiService.processPendingDocumentsBatch(pendingIds, sessionId || paypalToken);
          
          // Clear stored data
          clearQueue();
          localStorage.removeItem(BATCH_STORAGE_KEY);
          localStorage.removeItem(VISA_TYPE_KEY);
          localStorage.removeItem('stripe_session_id');
          localStorage.removeItem('paypal_order_id');
          localStorage.removeItem('payment_provider');
          
          setStatus('complete');
          setStatusMessage('Analysis complete!');
          
          // Navigate to results
          setTimeout(() => {
            navigate('/batch-results', {
              state: {
                batchResult: result,
                visaType
              }
            });
          }, 1500);
        } else {
          // Single document processing
          setStatusMessage('Analyzing document...');
          
          const result = await apiService.processPendingDocument(singlePendingId, sessionId || paypalToken);
          
          // Clear stored data
          clearQueue();
          localStorage.removeItem(SINGLE_STORAGE_KEY);
          localStorage.removeItem('stripe_session_id');
          localStorage.removeItem('paypal_order_id');
          localStorage.removeItem('payment_provider');
          
          setStatus('complete');
          setStatusMessage('Analysis complete!');
          
          // Navigate to results
          setTimeout(() => {
            navigate('/results', {
              state: {
                analysis: result.analysis,
                documentId: result.document_id
              }
            });
          }, 1500);
        }
      } catch (err) {
        console.error('Processing error:', err);
        setStatus('error');
        setError(err.response?.data?.detail || err.message || 'Processing failed');
      }
    };

    processDocuments();
  }, [sessionId, paypalToken, payerId, provider, navigate, clearQueue, trackEvent]);

  const getStatusIcon = () => {
    switch (status) {
      case 'verifying':
      case 'processing':
        return <Loader2 className="w-16 h-16 text-trust animate-spin" />;
      case 'complete':
        return <CheckCircle2 className="w-16 h-16 text-verify" />;
      case 'error':
        return <XCircle className="w-16 h-16 text-destructive" />;
      default:
        return <Loader2 className="w-16 h-16 text-trust animate-spin" />;
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case 'verifying':
        return 'Verifying Payment';
      case 'processing':
        return 'Analyzing Documents';
      case 'complete':
        return 'Analysis Complete!';
      case 'error':
        return 'Something Went Wrong';
      default:
        return 'Processing';
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-trust" />
            <span className="font-outfit font-bold text-xl text-slate-900">Visa-Ready</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <motion.div
            animate={status === 'processing' || status === 'verifying' ? { rotate: 360 } : {}}
            transition={{ duration: 2, repeat: status === 'processing' || status === 'verifying' ? Infinity : 0, ease: 'linear' }}
            className="mb-6 inline-block"
          >
            {getStatusIcon()}
          </motion.div>

          <h1 className="font-outfit font-bold text-2xl text-slate-900 mb-2">
            {getStatusTitle()}
          </h1>
          
          <p className="font-public text-slate-600 mb-6">
            {statusMessage}
          </p>

          {status === 'error' && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
              <p className="text-destructive text-sm">{error}</p>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-3">
              <button
                onClick={() => navigate('/documents')}
                className="w-full bg-trust text-trust-foreground px-6 py-3 rounded-md font-public font-semibold hover:bg-trust/90 transition-colors flex items-center justify-center gap-2"
              >
                <FileText className="w-5 h-5" />
                Try Again
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full border border-slate-300 text-slate-700 px-6 py-3 rounded-md font-public font-semibold hover:bg-slate-50 transition-colors"
              >
                Return to Home
              </button>
            </div>
          )}

          {(status === 'verifying' || status === 'processing') && (
            <div className="mt-8">
              <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <motion.div
                  className="h-full bg-trust rounded-full"
                  initial={{ width: '0%' }}
                  animate={{ width: status === 'verifying' ? '40%' : '80%' }}
                  transition={{ duration: 0.5 }}
                />
              </div>
              <p className="text-sm text-slate-500 mt-2">
                {status === 'verifying' ? 'Step 1 of 2' : 'Step 2 of 2'}
              </p>
            </div>
          )}

          {status === 'complete' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-sm text-slate-500"
            >
              Redirecting to your results...
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
